import { cn } from '@atlas/ui-primitives'
import { useState } from 'react'
import { isPreviewable, typeMarker } from './asset-format'

/**
 * The grid these tiles sit in, owned here so every asset section — and the skeleton
 * standing in for one — lays its tiles out on the same columns.
 */
export const ASSET_TILE_GRID = 'grid grid-cols-3 gap-3 sm:grid-cols-4 lg:grid-cols-6'

/**
 * The asset-list card's own, looser grid. Deliberately not the same decision as
 * {@link ASSET_TILE_GRID}: the delivered grid is an inventory where a screenful of
 * tiles is the point, while a listed tile has to be recognisable as a specific logo or
 * packaging shot, and has to carry a readable label when no thumbnail resolves.
 */
export const ASSET_LIST_TILE_GRID = 'grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4'

export interface AssetThumbTileProps {
	/**
	 * Names the tile for anyone not reading the grid visually — the visible marker is a
	 * ratio or a type, which alone identifies nothing.
	 */
	fileName: string
	contentType: string
	/** A presigned thumbnail URL; null or absent while none resolved. */
	previewUrl?: string | null
	/** Replaces the type marker when the tile has a more telling label, e.g. an aspect ratio. */
	marker?: string | null
	onClick: () => void
	className?: string
}

/**
 * One small square asset tile: its thumbnail when one resolved, a short marker
 * otherwise. Shared by the Product page's delivered-assets grid and the Library.
 */
export function AssetThumbTile({ fileName, contentType, previewUrl, marker, onClick, className }: AssetThumbTileProps) {
	/**
	 * The URL whose image failed to load. A presigned URL can 404 or expire before the
	 * browser fetches it, and a broken image says less than the marker does. Keyed by URL, so
	 * a fresh URL for the same file is attempted rather than inheriting the last failure.
	 */
	const [failedUrl, setFailedUrl] = useState<string | null>(null)
	const showPreview = Boolean(previewUrl) && isPreviewable(contentType) && previewUrl !== failedUrl

	return (
		<button
			type="button"
			onClick={onClick}
			aria-label={fileName}
			title={fileName}
			className={cn(
				'flex aspect-square items-center justify-center overflow-hidden rounded-ds-sm border border-ds-border bg-ds-surface transition-shadow hover:shadow-ds-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-ring',
				className,
			)}
		>
			{showPreview ? (
				<img
					src={previewUrl ?? undefined}
					alt={fileName}
					onError={() => setFailedUrl(previewUrl ?? null)}
					className="h-full w-full object-contain p-2"
				/>
			) : (
				<span className="font-mono text-ds-3xs uppercase text-ds-text-subtle">{marker ?? typeMarker(contentType)}</span>
			)}
		</button>
	)
}
